/**
 * THE GROCERY CLUB (TGC) - Core Theme Interactions & Bag System
 */
(function () {
  'use strict';

  const MIN_THRESHOLD = window.TGC_CONFIG?.minOrder || 600;

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  const formatMoney = (cents) => {
    const amount = (cents / 100);
    return 'HK$' + Math.round(amount).toLocaleString('en-HK');
  };

  /* ------------------------------------------------------------------------
     Language Switching Support
     ------------------------------------------------------------------------ */
  function initLanguageSwitchers() {
    $$('[data-tgc-lang]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const langCode = btn.getAttribute('data-tgc-lang');
        const form = btn.closest('form') || $('#localization_form') || document.querySelector('form[action*="localization"]');
        if (form) {
          const input = form.querySelector('input[name="locale_code"], input[name="language_code"]');
          if (input) {
            input.value = langCode;
            form.submit();
            return;
          }
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Mega Menu & Mobile Navigation
     ------------------------------------------------------------------------ */
  function initNavigation() {
    const navShop = $('#tgcNavShop');
    const mega = $('#tgcMega');
    const burger = $('#tgcBurger');

    if (navShop && mega) {
      navShop.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = !mega.hasAttribute('hidden');
        if (isOpen) {
          mega.setAttribute('hidden', '');
          navShop.setAttribute('aria-expanded', 'false');
        } else {
          mega.removeAttribute('hidden');
          navShop.setAttribute('aria-expanded', 'true');
        }
      });

      document.addEventListener('click', (e) => {
        if (!mega.contains(e.target) && !navShop.contains(e.target) && (!burger || !burger.contains(e.target))) {
          mega.setAttribute('hidden', '');
          navShop.setAttribute('aria-expanded', 'false');
          if (burger) burger.setAttribute('aria-expanded', 'false');
        }
      });
    }

    if (burger && mega) {
      burger.addEventListener('click', (e) => {
        e.preventDefault();
        const isOpen = !mega.hasAttribute('hidden');
        if (isOpen) {
          mega.setAttribute('hidden', '');
          burger.setAttribute('aria-expanded', 'false');
        } else {
          mega.removeAttribute('hidden');
          burger.setAttribute('aria-expanded', 'true');
        }
      });
    }
  }

  /* ------------------------------------------------------------------------
     Live Cart, Drawer & Minimum Order Gate Meter
     ------------------------------------------------------------------------ */
  class TGCCartManager {
    constructor() {
      this.drawer = $('#tgcDrawer');
      this.scrim = $('#tgcScrim');
      this.bagBtn = $('#tgcBagBtn');
      this.closeBtn = $('#tgcDrawerClose');
      this.cartCount = $('#tgcBagCount');
      this.gateMeter = $('#tgcGate');
      this.gateFill = $('#tgcGateFill');
      this.gateText = $('#tgcGateText');

      this.drawerGate = $('#tgcDrawerGate');
      this.drawerFill = $('#tgcDrawerFill');
      this.drawerGateText = $('#tgcDrawerGateText');
      this.drawerBody = $('#tgcDrawerBody');
      this.drawerFoot = $('#tgcDrawerFoot');
      this.drawerSubtotal = $('#tgcDrawerSubtotal');
      this.checkoutBtn = $('#tgcDrawerCheckout');

      this.init();
    }

    init() {
      if (this.bagBtn) {
        this.bagBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.scrim) {
        this.scrim.addEventListener('click', () => this.close());
      }
      if (this.gateMeter) {
        this.gateMeter.addEventListener('click', () => this.open());
      }

      // Listen for cart refresh/updates from anywhere in the theme
      document.addEventListener('cart:refresh', () => this.refreshCart());
      document.addEventListener('cart:updated', (e) => {
        if (e.detail?.cart) {
          this.render(e.detail.cart);
        } else {
          this.refreshCart();
        }
      });

      // Intercept standalone Add to Cart buttons (outside product-form)
      document.addEventListener('click', (e) => {
        const atcBtn = e.target.closest('[data-tgc-add-to-cart]');
        if (atcBtn && !atcBtn.closest('product-form')) {
          e.preventDefault();
          const variantId = atcBtn.getAttribute('data-variant-id');
          const qty = parseInt(atcBtn.getAttribute('data-quantity') || '1', 10);
          if (variantId) {
            this.addItem(variantId, qty, atcBtn);
          }
        }

        // Cart drawer upsell slider navigation
        const prevBtn = e.target.closest('[data-upsell-prev]');
        const nextBtn = e.target.closest('[data-upsell-next]');
        if (prevBtn || nextBtn) {
          e.preventDefault();
          const wrapper = (prevBtn || nextBtn).closest('[data-tgc-cart-upsells]');
          const track = wrapper ? wrapper.querySelector('[data-upsell-track]') : null;
          if (track) {
            const scrollAmount = track.clientWidth * 0.75;
            track.scrollBy({
              left: prevBtn ? -scrollAmount : scrollAmount,
              behavior: 'smooth'
            });
          }
        }
      });

      // Fetch initial cart state
      this.refreshCart();
    }

    open() {
      // 1. Open TGC custom drawer if present
      if (this.drawer) {
        this.drawer.classList.add('on');
        this.drawer.removeAttribute('hidden');
      }
      if (this.scrim) {
        this.scrim.classList.add('on');
        this.scrim.removeAttribute('hidden');
      }

      // 2. Open Minimog native cart drawer
      const minimogDrawer = document.querySelector('m-cart-drawer');
      if (minimogDrawer && typeof minimogDrawer.open === 'function') {
        minimogDrawer.open();
      }

      document.body.style.overflow = 'hidden';
    }

    close() {
      if (this.drawer) {
        this.drawer.classList.remove('on');
      }
      if (this.scrim) {
        this.scrim.classList.remove('on');
      }

      const minimogDrawer = document.querySelector('m-cart-drawer');
      if (minimogDrawer && typeof minimogDrawer.close === 'function') {
        minimogDrawer.close();
      }

      setTimeout(() => {
        if (this.drawer && !this.drawer.classList.contains('on')) {
          this.drawer.setAttribute('hidden', '');
        }
        if (this.scrim && !this.scrim.classList.contains('on')) {
          this.scrim.setAttribute('hidden', '');
        }
        document.body.style.overflow = '';
      }, 350);
    }

    showToast(message = 'Product added to cart successfully') {
      const existingToast = document.querySelector('.tgc-floating-toast');
      if (existingToast) existingToast.remove();

      const toast = document.createElement('div');
      toast.className = 'tgc-floating-toast';
      toast.innerHTML = `
        <span class="tgc-toast-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </span>
        <span class="tgc-toast-text">${message}</span>
        <button type="button" class="tgc-toast-close" aria-label="Close notification">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      `;

      document.body.appendChild(toast);
      
      const closeBtn = toast.querySelector('.tgc-toast-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          toast.classList.remove('show');
          setTimeout(() => toast.remove(), 250);
        });
      }

      requestAnimationFrame(() => {
        toast.classList.add('show');
      });

      setTimeout(() => {
        if (toast.parentElement) {
          toast.classList.remove('show');
          setTimeout(() => toast.remove(), 250);
        }
      }, 3000);
    }

    async refreshCart() {
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await res.json();
        this.render(cart);
      } catch (err) {
        console.warn('TGC: Failed to fetch cart', err);
      }
    }

    async addItem(variantId, quantity = 1, btnEl = null) {
      // 1. Flying Image Animation Effect
      if (btnEl) {
        const slideEl = btnEl.closest('.tgc-cart-upsell__slide') || btnEl.closest('.tgc-cart-upsell__card');
        const img = slideEl ? slideEl.querySelector('img') : null;
        const targetCart = document.querySelector('.m-cart-drawer__items') || document.querySelector('#MinimogCartDrawerBody') || document.querySelector('#MinimogCartDrawerHeader');

        if (img && targetCart) {
          const imgRect = img.getBoundingClientRect();
          const targetRect = targetCart.getBoundingClientRect();

          const flyingImg = img.cloneNode(true);
          flyingImg.style.cssText = `
            position: fixed !important;
            top: ${imgRect.top}px !important;
            left: ${imgRect.left}px !important;
            width: ${imgRect.width}px !important;
            height: ${imgRect.height}px !important;
            border-radius: 12px !important;
            z-index: 9999999 !important;
            pointer-events: none !important;
            transition: all 0.65s cubic-bezier(0.2, 0.9, 0.3, 1) !important;
            box-shadow: 0 10px 25px rgba(122, 21, 38, 0.35) !important;
            transform-origin: center center !important;
          `;
          document.body.appendChild(flyingImg);

          requestAnimationFrame(() => {
            flyingImg.style.top = `${targetRect.top + 20}px`;
            flyingImg.style.left = `${targetRect.left + 20}px`;
            flyingImg.style.width = '42px';
            flyingImg.style.height = '42px';
            flyingImg.style.transform = 'scale(0.4) rotate(-15deg)';
            flyingImg.style.opacity = '0';
          });

          setTimeout(() => {
            flyingImg.remove();
          }, 700);
        }

        btnEl.disabled = true;
        const originalText = btnEl.textContent;
        btnEl.classList.add('ok');
        btnEl.textContent = btnEl.getAttribute('data-added-text') || 'Added';
        
        // Instantly animate and remove the clicked upsell product card
        if (slideEl) {
          slideEl.style.transition = 'all 0.35s ease';
          slideEl.style.opacity = '0';
          slideEl.style.transform = 'scale(0.7)';
          setTimeout(() => {
            slideEl.remove();
            const track = document.querySelector('[data-upsell-track]');
            if (track && track.children.length === 0) {
              const wrapper = document.querySelector('[data-tgc-cart-upsells]');
              if (wrapper) wrapper.style.display = 'none';
            }
          }, 350);
        }

        setTimeout(() => {
          btnEl.classList.remove('ok');
          btnEl.textContent = originalText;
          btnEl.disabled = false;
        }, 1200);
      }

      // If demo card placeholder ID, simulate cart addition and open drawer
      if (String(variantId).startsWith('demo-')) {
        this.open();
        return;
      }

      try {
        let sectionsToBundle = ['cart-drawer', 'cart-count'];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
        const sectionsStr = [...new Set(sectionsToBundle)].join(',');

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);
        formData.append('sections', sectionsStr);
        formData.append('sections_url', window.location.pathname);

        const addUrl = window.MinimogSettings?.routes?.cart_add_url || '/cart/add';
        const res = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript, application/json, text/javascript, */*',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        const response = await res.json();

        if (response.status && response.status >= 400) {
          console.error('TGC: Add to cart error:', response.description || response.message);
          return;
        }

        // Get rendered HTML sections directly from add response or fetch fresh
        let drawerHtml = response?.sections && response.sections['cart-drawer'] ? response.sections['cart-drawer'] : null;
        let countHtml = response?.sections && response.sections['cart-count'] ? response.sections['cart-count'] : null;

        if (!drawerHtml) {
          try {
            const rootUrl = window.Shopify?.routes?.root || window.MinimogSettings?.routes?.root || '/';
            const sectionsRes = await fetch(`${rootUrl}?sections[]=cart-drawer&sections[]=cart-count&_t=${Date.now()}`);
            const sectionsData = await sectionsRes.json();
            if (sectionsData) {
              drawerHtml = sectionsData['cart-drawer'];
              countHtml = sectionsData['cart-count'];
              if (!response.sections) response.sections = {};
              response.sections['cart-drawer'] = drawerHtml;
              response.sections['cart-count'] = countHtml;
            }
          } catch (secErr) {
            console.warn('TGC: Error fetching drawer sections', secErr);
          }
        }

        if (drawerHtml) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(drawerHtml, 'text/html');

          // Update Drawer Items & Body
          const newBody = doc.querySelector('#MinimogCartDrawerBody');
          const curBody = document.querySelector('#MinimogCartDrawerBody');
          if (newBody && curBody) {
            curBody.innerHTML = newBody.innerHTML;
          }

          // Update Drawer Footer (Subtotal price, checkout buttons, addons)
          const newFooter = doc.querySelector('#MinimogCartDrawerFooter');
          const curFooter = document.querySelector('#MinimogCartDrawerFooter');
          if (newFooter && curFooter) {
            curFooter.innerHTML = newFooter.innerHTML;
          }

          // Update Drawer Header
          const newHeader = doc.querySelector('#MinimogCartDrawerHeader');
          const curHeader = document.querySelector('#MinimogCartDrawerHeader');
          if (newHeader && curHeader) {
            curHeader.innerHTML = newHeader.innerHTML;
          }
        }

        // Notify Minimog theme pubSub events
        if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
            cart: response,
            productVariantId: variantId
          });
        }

        this.showToast(window.MinimogStrings?.itemAdded || 'Product added to cart successfully');

        // Dispatch cart:updated, cart:refresh, and product-ajax:added
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              cart: response
            }
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              open: true,
              cart: response
            }
          })
        );

        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            detail: {
              product: response
            }
          })
        );

        // Update local cart state & open drawer
        await this.refreshCart();
        this.open();
      } catch (err) {
        console.error('TGC: Failed to add item to cart', err);
      }
    }

    async updateItem(key, quantity) {
      try {
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
        if (!sectionsToBundle.includes('cart-drawer')) sectionsToBundle.push('cart-drawer');
        if (!sectionsToBundle.includes('cart-count')) sectionsToBundle.push('cart-count');

        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: key, quantity, sections: sectionsToBundle })
        });
        const cart = await res.json();

        if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart });
        }
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false, cart } }));

        this.render(cart);
      } catch (err) {
        console.error('TGC: Failed to update item', err);
      }
    }

    render(cart) {
      const totalAmount = (cart.total_price || 0) / 100;
      const count = cart.item_count || 0;

      // Update badge counts across page
      if (this.cartCount) {
        this.cartCount.textContent = count;
        if (count > 0) {
          this.cartCount.removeAttribute('hidden');
        } else {
          this.cartCount.setAttribute('hidden', '');
        }
      }
      document.querySelectorAll('.m-cart-count-bubble, .m-cart-count, [data-cart-count]').forEach((el) => {
        el.textContent = count;
      });

      // Update Top Gate Meter
      const pct = Math.min(100, Math.round((totalAmount / MIN_THRESHOLD) * 100));
      const remaining = Math.max(0, MIN_THRESHOLD - totalAmount);

      if (this.gateFill) {
        this.gateFill.style.width = pct + '%';
      }
      if (this.gateText) {
        if (totalAmount >= MIN_THRESHOLD) {
          this.gateText.textContent = window.TGC_TRANSLATIONS?.gate_open || 'Minimum reached. Checkout’s open.';
          if (this.gateMeter) this.gateMeter.classList.add('done');
        } else {
          const template = window.TGC_TRANSLATIONS?.gate_to_go || '{x} to go before checkout opens';
          this.gateText.textContent = template.replace('{x}', formatMoney(remaining * 100));
          if (this.gateMeter) this.gateMeter.classList.remove('done');
        }
      }

      // Update Drawer Gate Meter
      if (this.drawerFill) {
        this.drawerFill.style.width = pct + '%';
      }
      if (this.drawerGateText) {
        if (totalAmount >= MIN_THRESHOLD) {
          this.drawerGateText.textContent = window.TGC_TRANSLATIONS?.d_gate_ok || 'You’re over HK$600. Checkout’s open.';
          if (this.drawerGate) this.drawerGate.classList.add('done');
        } else {
          const template = window.TGC_TRANSLATIONS?.d_gate || '{x} to go. Our minimum order is HK$600.';
          this.drawerGateText.textContent = template.replace('{x}', formatMoney(remaining * 100));
          if (this.drawerGate) this.drawerGate.classList.remove('done');
        }
      }

      // Update Drawer Body
      if (this.drawerBody) {
        if (count === 0) {
          this.drawerBody.innerHTML = `
            <div class="tgc-empty" style="padding: 48px 0; text-align: center; display: grid; gap: 16px; justify-items: center;">
              <p style="margin: 0; font-size: 18px; color: var(--tgc-ink); font-weight: 500;">
                ${window.TGC_TRANSLATIONS?.d_empty || 'Your bag is empty. Dinner won’t order itself.'}
              </p>
              <a href="/collections/all" class="tgc-btn" style="min-height: 44px; padding: 0 24px;">
                ${window.TGC_TRANSLATIONS?.d_start || 'Start shopping'}
              </a>
            </div>
          `;
          if (this.drawerFoot) this.drawerFoot.setAttribute('hidden', '');
        } else {
          if (this.drawerFoot) this.drawerFoot.removeAttribute('hidden');

          let html = '';
          cart.items.forEach((item) => {
            const itemPriceFormatted = formatMoney(item.final_line_price);
            const unitPriceFormatted = formatMoney(item.final_price);

            html += `
              <div class="tgc-line" data-item-key="${item.key}">
                <div class="tgc-frame tgc-r11" style="width: 80px; background: var(--tgc-ground-2);">
                  <img src="${item.featured_image?.url || item.image || ''}" alt="${item.title}" width="80" height="80">
                </div>
                <div>
                  <h3 style="margin: 0; font-size: 15px; font-weight: 600; line-height: 1.3;">
                    <a href="${item.url}" style="color: inherit; text-decoration: none;">${item.product_title}</a>
                  </h3>
                  ${item.variant_title && item.variant_title !== 'Default Title' ? `<div style="font-size: 13px; color: var(--tgc-ink-soft); margin-top: 2px;">${item.variant_title}</div>` : ''}
                  ${item.selling_plan_allocation ? `<div style="font-size: 12px; font-weight: 600; color: var(--tgc-ragu); margin-top: 3px; display: inline-flex; align-items: center; gap: 4px;"><svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" style="vertical-align: middle;"><path d="M2 8a6 6 0 1 1 10.2 4.2L14 14l-4.2-1.8A6 6 0 0 1 2 8z"/></svg>${item.selling_plan_allocation.selling_plan.name}</div>` : ''}
                  <div class="l-pack" style="font-size: 13px; color: var(--tgc-ink-soft); margin-top: 2px;">
                    ${unitPriceFormatted}
                  </div>
                </div>
                <div class="tgc-l-right">
                  <div style="font-weight: 700; font-variant-numeric: tabular-nums;">${itemPriceFormatted}</div>
                  <div class="tgc-qty">
                    <button type="button" class="tgc-qty-btn minus" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease">&minus;</button>
                    <span>${item.quantity}</span>
                    <button type="button" class="tgc-qty-btn plus" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase">&plus;</button>
                  </div>
                  <button type="button" class="tgc-l-rm" data-key="${item.key}">${window.TGC_TRANSLATIONS?.d_remove || 'Remove'}</button>
                </div>
              </div>
            `;
          });

          this.drawerBody.innerHTML = html;

          // Attach listeners for line item controls
          this.drawerBody.querySelectorAll('.tgc-qty-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
              const key = btn.getAttribute('data-key');
              const qty = parseInt(btn.getAttribute('data-qty'), 10);
              this.updateItem(key, Math.max(0, qty));
            });
          });

          this.drawerBody.querySelectorAll('.tgc-l-rm').forEach((btn) => {
            btn.addEventListener('click', () => {
              const key = btn.getAttribute('data-key');
              this.updateItem(key, 0);
            });
          });
        }
      }

      // Update Subtotal & Checkout button state
      if (this.drawerSubtotal) {
        this.drawerSubtotal.textContent = formatMoney(cart.total_price);
      }

      if (this.checkoutBtn) {
        if (totalAmount >= MIN_THRESHOLD) {
          this.checkoutBtn.removeAttribute('disabled');
        } else {
          this.checkoutBtn.setAttribute('disabled', 'disabled');
        }
      }
    }
  }

  /* ------------------------------------------------------------------------
     Track Horizontal Scrolling (Arrows Support)
     ------------------------------------------------------------------------ */
  function initProductTracks() {
    $$('.tgc-track-container').forEach((container) => {
      const track = container.querySelector('.tgc-track');
      const prev = container.querySelector('.tgc-arrow.prev');
      const next = container.querySelector('.tgc-arrow.next');

      if (!track || !prev || !next) return;

      const updateArrows = () => {
        prev.disabled = track.scrollLeft <= 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };

      prev.addEventListener('click', () => {
        track.scrollBy({ left: -track.clientWidth * 0.75, behavior: 'smooth' });
      });

      next.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth * 0.75, behavior: 'smooth' });
      });

      track.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();
    });
  }

  /* ------------------------------------------------------------------------
     Accordion Toggle (FAQs & Product Details)
     ------------------------------------------------------------------------ */
  function initAccordions() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.tgc-accordion-btn');
      if (!btn) return;
      const content = btn.nextElementSibling;
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';

      btn.setAttribute('aria-expanded', !isExpanded);
      if (content) {
        content.hidden = isExpanded;
      }
      const icon = btn.querySelector('svg');
      if (icon) {
        icon.style.transform = isExpanded ? 'rotate(0deg)' : 'rotate(180deg)';
      }
    });
  }

  /* ------------------------------------------------------------------------
     Hero Slider & Carousel
     ------------------------------------------------------------------------ */
  function initHeroSlider() {
    const heroes = document.querySelectorAll('.tgc-hero[data-slider="true"]');
    heroes.forEach((hero) => {
      const slides = hero.querySelectorAll('.tgc-hero-slide');
      if (slides.length <= 1) return;

      const dots = hero.querySelectorAll('.tgc-hero-dot');
      const prevBtn = hero.querySelector('.tgc-hero-arrow-prev');
      const nextBtn = hero.querySelector('.tgc-hero-arrow-next');
      const autoplay = hero.dataset.autoplay === 'true';
      const interval = parseInt(hero.dataset.speed, 10) || 5000;

      let current = 0;
      let timer = null;

      function goTo(index) {
        if (index < 0) index = slides.length - 1;
        if (index >= slides.length) index = 0;
        current = index;

        slides.forEach((s, i) => {
          const isAct = i === current;
          s.classList.toggle('is-active', isAct);
          s.setAttribute('aria-hidden', !isAct);
          const vid = s.querySelector('video');
          if (vid) {
            if (isAct) {
              vid.play().catch(() => {});
            } else {
              vid.pause();
            }
          }
        });

        dots.forEach((d, i) => {
          d.classList.toggle('is-active', i === current);
          d.setAttribute('aria-current', i === current ? 'true' : 'false');
        });
      }

      function startTimer() {
        if (!autoplay) return;
        stopTimer();
        timer = setInterval(() => {
          goTo(current + 1);
        }, interval);
      }

      function stopTimer() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          goTo(current - 1);
          startTimer();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          goTo(current + 1);
          startTimer();
        });
      }

      dots.forEach((dot, idx) => {
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          goTo(idx);
          startTimer();
        });
      });

      hero.addEventListener('mouseenter', stopTimer);
      hero.addEventListener('mouseleave', startTimer);

      let touchStartX = 0;
      let touchEndX = 0;
      hero.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
        stopTimer();
      }, { passive: true });

      hero.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 40) {
          if (diff > 0) {
            goTo(current + 1);
          } else {
            goTo(current - 1);
          }
        }
        startTimer();
      }, { passive: true });

      // Shopify Theme Customizer events
      document.addEventListener('shopify:block:select', (e) => {
        if (!hero.contains(e.target)) return;
        const targetSlide = e.target.closest('.tgc-hero-slide') || e.target;
        const idx = Array.from(slides).indexOf(targetSlide);
        if (idx !== -1) {
          goTo(idx);
          stopTimer();
        }
      });

      document.addEventListener('shopify:block:deselect', (e) => {
        if (hero.contains(e.target)) {
          startTimer();
        }
      });

      startTimer();
    });
  }

  /* ------------------------------------------------------------------------
     Hero Parallax & Motion (Content-only smoothly moves with cursor)
     ------------------------------------------------------------------------ */
  function initHeroMotion() {
    const heroes = document.querySelectorAll('.tgc-hero[data-mouse-motion="true"]');
    if (!heroes.length) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    heroes.forEach((hero) => {
      let tx = 0, ty = 0, cx = 0, cy = 0, raf = 0;

      function tick() {
        cx += (tx - cx) * 0.08;
        cy += (ty - cy) * 0.08;

        const activeCopy = hero.querySelector('.tgc-hero-slide.is-active .tgc-hero-copy') || hero.querySelector('.tgc-hero-copy');
        if (activeCopy) {
          activeCopy.style.transform = `translate3d(${-cx * 8}px, ${-cy * 5}px, 0)`;
        }

        if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) {
          raf = requestAnimationFrame(tick);
        } else {
          raf = 0;
        }
      }

      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        tx = (e.clientX - r.left) / r.width * 2 - 1;
        ty = (e.clientY - r.top) / r.height * 2 - 1;
        if (!raf) raf = requestAnimationFrame(tick);
      });

      hero.addEventListener('pointerleave', () => {
        tx = 0;
        ty = 0;
        if (!raf) raf = requestAnimationFrame(tick);
      });
    });
  }

  /* ------------------------------------------------------------------------
     Shopify Subscriptions & Dynamic Price Controller
     ------------------------------------------------------------------------ */
  function initSubscriptionToggles() {
    // 1. Product Cards Subscription Toggle
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-tgc-card-sub-toggle]');
      if (!toggleBtn) return;

      e.preventDefault();
      const card = toggleBtn.closest('[data-tgc-card]');
      if (!card) return;

      const isCurrentlyActive = card.classList.contains('is-sub-active');
      const shouldActivate = !isCurrentlyActive;

      toggleBtn.setAttribute('aria-checked', shouldActivate ? 'true' : 'false');
      card.classList.toggle('is-sub-active', shouldActivate);

      // Elements inside the card
      const ppsCurrent = card.querySelector('.tgc-pps-current');
      const ppsOrig = card.querySelector('.tgc-pps-original');
      const packCurrent = card.querySelector('.tgc-pack-current');
      const packOrig = card.querySelector('.tgc-pack-original');
      const atcBtn = card.querySelector('.m-add-to-cart');
      const atcText = atcBtn ? atcBtn.querySelector('.m-add-to-cart--text') : null;
      const planInput = card.querySelector('[data-tgc-card-selling-plan-input]');
      const freqSelect = card.querySelector('[data-tgc-card-plan-select]');
      const freqWrap = card.querySelector('[data-tgc-card-frequency]');

      const regularPrice = parseInt(card.getAttribute('data-regular-price') || '0', 10);
      const regularPps = parseInt(card.getAttribute('data-regular-pps') || '0', 10);
      const discountedPrice = parseInt(card.getAttribute('data-discounted-price') || '0', 10);
      const discountedPps = parseInt(card.getAttribute('data-discounted-pps') || '0', 10);

      if (shouldActivate) {
        if (ppsCurrent) ppsCurrent.textContent = formatMoney(discountedPps);
        if (ppsOrig) ppsOrig.textContent = formatMoney(regularPps);
        if (packCurrent) packCurrent.textContent = formatMoney(discountedPrice);
        if (packOrig) packOrig.textContent = formatMoney(regularPrice);

        if (planInput) {
          if (freqSelect && freqSelect.value) {
            planInput.value = freqSelect.value;
          }
          planInput.disabled = false;
        }

        if (freqWrap) freqWrap.style.display = 'block';

        if (atcBtn && atcText) {
          atcText.textContent = atcBtn.getAttribute('data-sub-text') || 'Subscribe & Save';
        }
      } else {
        if (ppsCurrent) ppsCurrent.textContent = formatMoney(regularPps);
        if (packCurrent) packCurrent.textContent = formatMoney(regularPrice);

        if (planInput) {
          planInput.disabled = true;
        }

        if (freqWrap) freqWrap.style.display = 'none';

        if (atcBtn && atcText) {
          atcText.textContent = atcBtn.getAttribute('data-default-text') || 'Add';
        }
      }
    });

    // Card frequency dropdown change
    document.addEventListener('change', (e) => {
      const freqSelect = e.target.closest('[data-tgc-card-plan-select]');
      if (!freqSelect) return;

      const card = freqSelect.closest('[data-tgc-card]');
      if (!card) return;

      const planInput = card.querySelector('[data-tgc-card-selling-plan-input]');
      if (planInput) {
        planInput.value = freqSelect.value;
      }

      const selectedOption = freqSelect.options[freqSelect.selectedIndex];
      const discount = parseInt(selectedOption.getAttribute('data-discount') || '10', 10);
      const regularPrice = parseInt(card.getAttribute('data-regular-price') || '0', 10);
      const servings = parseInt(card.getAttribute('data-servings') || '4', 10);

      const discountedPrice = Math.round(regularPrice * (100 - discount) / 100);
      const discountedPps = Math.round(discountedPrice / servings);

      const ppsCurrent = card.querySelector('.tgc-pps-current');
      const packCurrent = card.querySelector('.tgc-pack-current');
      if (ppsCurrent) ppsCurrent.textContent = formatMoney(discountedPps);
      if (packCurrent) packCurrent.textContent = formatMoney(discountedPrice);
    });

    // 2. Product Details Page (PDP) Subscription Controls
    const pdpBox = document.querySelector('[data-tgc-pdp-subscription-box]');
    if (pdpBox) {
      const priceBox = document.querySelector('[data-tgc-pdp-price-box]');
      const servingVal = priceBox ? priceBox.querySelector('.tgc-pdp-serving-val') : null;
      const servingOrig = priceBox ? priceBox.querySelector('.tgc-pdp-serving-orig') : null;
      const packVal = priceBox ? priceBox.querySelector('.tgc-pdp-pack-val') : null;
      const packOrig = priceBox ? priceBox.querySelector('.tgc-pdp-pack-orig') : null;
      const atcText = document.querySelector('[data-tgc-pdp-atc-text]');
      const atcBtn = atcText ? atcText.closest('.m-add-to-cart') : null;
      const planSelect = document.querySelector('[data-tgc-pdp-plan-select]');
      const freqWrap = document.querySelector('[data-tgc-pdp-frequency]');

      const regularPrice = parseInt(priceBox?.getAttribute('data-regular-price') || '0', 10);
      const servings = parseInt(priceBox?.getAttribute('data-servings') || '4', 10);
      const regularPps = Math.round(regularPrice / (servings || 1));

      pdpBox.addEventListener('change', (e) => {
        const radio = e.target.closest('input[name="purchase_type"]');
        if (!radio) return;

        const isSub = radio.value === 'subscription';

        pdpBox.querySelectorAll('.tgc-sub-option').forEach(opt => {
          opt.classList.toggle('is-active', opt.querySelector('input[type="radio"]') === radio);
        });

        if (freqWrap) {
          freqWrap.style.display = isSub ? 'block' : 'none';
        }

        if (planSelect) {
          planSelect.disabled = !isSub;
        }

        if (isSub) {
          const selectedOption = planSelect?.tagName === 'SELECT' ? planSelect.options[planSelect.selectedIndex] : null;
          const discount = parseInt(selectedOption?.getAttribute('data-discount') || '10', 10);
          const subPrice = Math.round(regularPrice * (100 - discount) / 100);
          const subPps = Math.round(subPrice / (servings || 1));

          if (servingVal) servingVal.textContent = formatMoney(subPps);
          if (servingOrig) {
            servingOrig.textContent = formatMoney(regularPps);
            servingOrig.style.display = 'inline-block';
          }
          if (packVal) packVal.textContent = formatMoney(subPrice);
          if (packOrig) {
            packOrig.textContent = formatMoney(regularPrice);
            packOrig.style.display = 'inline-block';
          }

          if (atcText && atcBtn) {
            const subLabel = atcBtn.getAttribute('data-sub-text') || 'Subscribe now';
            atcText.textContent = `${subLabel} · ${formatMoney(subPrice)}`;
          }
        } else {
          if (servingVal) servingVal.textContent = formatMoney(regularPps);
          if (servingOrig) servingOrig.style.display = 'none';
          if (packVal) packVal.textContent = formatMoney(regularPrice);
          if (packOrig) packOrig.style.display = 'none';

          if (atcText && atcBtn) {
            const regLabel = atcBtn.getAttribute('data-regular-text') || 'Add to bag';
            atcText.textContent = `${regLabel} · ${formatMoney(regularPrice)}`;
          }
        }
      });

      if (planSelect && planSelect.tagName === 'SELECT') {
        planSelect.addEventListener('change', () => {
          const radio = pdpBox.querySelector('input[value="subscription"]');
          if (radio && radio.checked) {
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
    }
  }

  /* ------------------------------------------------------------------------
     DOM Ready Init
     ------------------------------------------------------------------------ */
  function domReady() {
    initLanguageSwitchers();
    initNavigation();
    window.tgcCart = new TGCCartManager();
    initProductTracks();
    initAccordions();
    initHeroSlider();
    initHeroMotion();
    initSubscriptionToggles();
  }

  // Support Shopify section reloads in theme editor
  document.addEventListener('shopify:section:load', () => {
    initHeroSlider();
    initHeroMotion();
    initProductTracks();
    initAccordions();
    initSubscriptionToggles();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', domReady);
  } else {
    domReady();
  }
})();
